/**
 * SizilPro — useProposals
 * TanStack Query hooks for proposals and proposal dashboard stats.
 *
 * File: src/modules/proposal-builder/hooks/useProposals.ts
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/store/authStore";
import type {
  Proposal,
  ProposalFull,
  ProposalStatus,
  BuilderMode,
  BuilderDocumentSettings,
  ClientType,
  FixedPagePolicy,
  PriceUnit,
  ProposalDocumentLayout,
  ProposalTemplate,
  TemplateLineItem,
  TemplatePage,
  TemplateService,
  TemplateStaticPage,
} from "../types";

type ProposalPipelineStats = {
  total: number;
  draft: number;
  sent: number;
  viewed: number;
  signed: number;
  declined: number;
  expired: number;
  total_value: number;
  signed_value: number;
  conversion_rate: number;
};

const proposalsListKey = (
  clientId: string | null,
  filters?: { status?: ProposalStatus }
) => ["proposals", clientId ?? "no-client", "list", filters ?? {}] as const;

const proposalDetailKey = (id: string | null) =>
  ["proposals", "detail", id ?? "invalid"] as const;

const pipelineStatsKey = (clientId: string | null) =>
  ["proposals", clientId ?? "no-client", "pipeline-stats"] as const;

const monthlyProposalCountKey = (clientId: string | null) =>
  ["proposals", clientId ?? "no-client", "monthly-count"] as const;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const CLIENT_TYPES: ClientType[] = [
  "construction",
  "restaurant",
  "commercial",
  "residential",
  "other",
];

const PRICE_UNITS: PriceUnit[] = [
  "per_clean",
  "per_hour",
  "per_sqft",
  "per_visit",
  "fixed",
];

const BUILDER_MODES: BuilderMode[] = ["prebuilt", "drag_drop"];
const FIXED_PAGE_POLICIES: FixedPagePolicy[] = [
  "template_defined",
  "brand_defined",
  "mixed",
];

function isUuid(value: string | undefined | null): value is string {
  return typeof value === "string" && UUID_RE.test(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getString(
  source: Record<string, unknown>,
  ...keys: string[]
): string | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
}

function getNumber(
  source: Record<string, unknown>,
  ...keys: string[]
): number | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return null;
}

function toClientType(value: string | null): ClientType | null {
  if (!value) return null;
  return CLIENT_TYPES.includes(value as ClientType)
    ? (value as ClientType)
    : "other";
}

function toPriceUnit(value: string | null): PriceUnit | null {
  if (!value) return null;
  return PRICE_UNITS.includes(value as PriceUnit)
    ? (value as PriceUnit)
    : null;
}

function toBuilderMode(value: string | null): BuilderMode {
  if (!value) return "prebuilt";
  return BUILDER_MODES.includes(value as BuilderMode)
    ? (value as BuilderMode)
    : "prebuilt";
}

function toFixedPagePolicy(value: string | null): FixedPagePolicy {
  if (!value) return "template_defined";
  return FIXED_PAGE_POLICIES.includes(value as FixedPagePolicy)
    ? (value as FixedPagePolicy)
    : "template_defined";
}

function toDocumentSettings(value: unknown): BuilderDocumentSettings {
  if (!isRecord(value)) {
    return {};
  }
  return value as BuilderDocumentSettings;
}

function toLayoutJson(value: unknown): ProposalDocumentLayout {
  if (!isRecord(value)) {
    return { pages: [] };
  }

  const pages = Array.isArray(value.pages) ? value.pages : [];
  return {
    ...(value as ProposalDocumentLayout),
    pages,
  };
}

function mapLegacyTemplateLineItems(value: unknown): TemplateLineItem[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item): TemplateLineItem | null => {
      if (!isRecord(item)) return null;

      const name = getString(item, "name");
      if (!name) return null;

      return {
        service_item_id: getString(item, "service_item_id"),
        name,
        description: getString(item, "description"),
        price: getNumber(item, "price"),
        price_unit: toPriceUnit(getString(item, "price_unit")),
        frequency: getString(item, "frequency"),
        duration_hours: getNumber(item, "duration_hours"),
        sort_order: getNumber(item, "sort_order") ?? 0,
      };
    })
    .filter((item): item is TemplateLineItem => item !== null);
}

function mapTemplateService(row: Record<string, unknown>): TemplateService | null {
  const id = getString(row, "id");
  const templateId = getString(row, "template_id");
  const serviceName = getString(row, "service_name");

  if (!id || !templateId || !serviceName) return null;

  return {
    id,
    template_id: templateId,
    service_item_id: getString(row, "service_item_id"),
    service_name: serviceName,
    service_description: getString(row, "service_description"),
    base_price: getNumber(row, "base_price"),
    price_unit: toPriceUnit(getString(row, "price_unit")),
    quantity: getNumber(row, "quantity") ?? 1,
    sort_order: getNumber(row, "sort_order") ?? 0,
    created_at: getString(row, "created_at") ?? "",
    updated_at: getString(row, "updated_at") ?? "",
  };
}

function mapTemplateStaticPage(
  row: Record<string, unknown>
): TemplateStaticPage | null {
  const id = getString(row, "id");
  const templateId = getString(row, "template_id");
  const title = getString(row, "title");

  if (!id || !templateId || !title) return null;

  return {
    id,
    template_id: templateId,
    static_page_id: getString(row, "static_page_id"),
    title,
    content_html: getString(row, "content_html"),
    page_order: getNumber(row, "page_order") ?? 0,
    created_at: getString(row, "created_at") ?? "",
    updated_at: getString(row, "updated_at") ?? "",
  };
}

function mapTemplatePage(row: Record<string, unknown>): TemplatePage | null {
  const id = getString(row, "id");
  const templateId = getString(row, "template_id");
  const title = getString(row, "title");
  const pageType = getString(row, "page_type");

  if (!id || !templateId || !title || !pageType) return null;

  return {
    id,
    template_id: templateId,
    title,
    page_type: pageType as TemplatePage["page_type"],
    page_order: getNumber(row, "page_order") ?? 0,
    source_static_page_id: getString(row, "source_static_page_id"),
    is_required:
      typeof row.is_required === "boolean" ? row.is_required : false,
    is_locked:
      typeof row.is_locked === "boolean" ? row.is_locked : false,
    include_in_preview:
      typeof row.include_in_preview === "boolean"
        ? row.include_in_preview
        : true,
    settings: isRecord(row.settings)
      ? (row.settings as TemplatePage["settings"])
      : {},
    created_at: getString(row, "created_at") ?? "",
    updated_at: getString(row, "updated_at") ?? "",
  };
}

function normalizeTemplateForProposal(
  value: unknown
): ProposalTemplate | null {
  if (!isRecord(value)) return null;

  const id = getString(value, "id");
  const clientId = getString(value, "client_id");
  const name = getString(value, "name");

  if (!id || !clientId || !name) return null;

  const templateServices = Array.isArray(value.template_services)
    ? value.template_services
        .map((row) => (isRecord(row) ? mapTemplateService(row) : null))
        .filter((row): row is TemplateService => row !== null)
    : [];

  const templateStaticPages = Array.isArray(value.template_static_pages)
    ? value.template_static_pages
        .map((row) => (isRecord(row) ? mapTemplateStaticPage(row) : null))
        .filter((row): row is TemplateStaticPage => row !== null)
    : [];

  const templatePages = Array.isArray(value.template_pages)
    ? value.template_pages
        .map((row) => (isRecord(row) ? mapTemplatePage(row) : null))
        .filter((row): row is TemplatePage => row !== null)
    : [];

  return {
    id,
    client_id: clientId,
    name,
    description: getString(value, "description"),
    category: getString(value, "category"),
    tax_rate: getNumber(value, "tax_rate"),

    builder_mode: toBuilderMode(getString(value, "builder_mode")),
    layout_version: getNumber(value, "layout_version") ?? 1,
    document_settings: toDocumentSettings(value.document_settings),
    layout_json: toLayoutJson(value.layout_json),
    preview_image_url: getString(value, "preview_image_url"),
    fixed_page_policy: toFixedPagePolicy(getString(value, "fixed_page_policy")),

    client_type: toClientType(getString(value, "client_type")),
    ai_tone: getString(value, "ai_tone"),
    ai_industry_context: getString(value, "ai_industry_context"),
    ai_service_context: getString(value, "ai_service_context"),
    default_line_items: mapLegacyTemplateLineItems(value.default_line_items),
    default_static_page_ids: Array.isArray(value.default_static_page_ids)
      ? value.default_static_page_ids.filter(
          (pageId): pageId is string => typeof pageId === "string"
        )
      : [],
    default_tax_rate: getNumber(value, "default_tax_rate") ?? 0,
    source_proposal_id: getString(value, "source_proposal_id"),
    is_default:
      typeof value.is_default === "boolean" ? value.is_default : false,
    created_by: getString(value, "created_by"),
    created_at: getString(value, "created_at") ?? "",
    updated_at: getString(value, "updated_at") ?? "",
    template_services: templateServices,
    template_static_pages: templateStaticPages,
    template_pages: templatePages,
  };
}

function emptyPipelineStats(): ProposalPipelineStats {
  return {
    total: 0,
    draft: 0,
    sent: 0,
    viewed: 0,
    signed: 0,
    declined: 0,
    expired: 0,
    total_value: 0,
    signed_value: 0,
    conversion_rate: 0,
  };
}

async function loadTemplateForProposal(templateId: string | null | undefined) {
  if (typeof templateId !== "string" || templateId.trim().length === 0) {
    return null;
  }

  const { data, error } = await supabase
    .from("proposal_templates")
    .select(
      `
      *,
      template_services (*),
      template_static_pages (*),
      template_pages (*)
    `
    )
    .eq("id", templateId)
    .single();

  if (error) throw error;
  return normalizeTemplateForProposal(data ?? null);
}

// ─── List proposals ───────────────────────────────────────────
export function useProposals(filters?: { status?: ProposalStatus }) {
  const clientId = useAuthStore((s) => s.clientId);

  return useQuery<Proposal[]>({
    queryKey: proposalsListKey(clientId, filters),
    queryFn: async () => {
      if (!clientId) return [];

      let query = supabase
        .from("proposals")
        .select("*")
        .eq("client_id", clientId)
        .order("updated_at", { ascending: false });

      if (filters?.status) {
        query = query.eq("status", filters.status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data ?? []) as Proposal[];
    },
    enabled: !!clientId,
    staleTime: 1000 * 30,
    refetchOnMount: true,
  });
}

// ─── Single proposal detail ───────────────────────────────────
export function useProposal(id: string | undefined) {
  const validId = isUuid(id) ? id : null;

  return useQuery<ProposalFull | null>({
    queryKey: proposalDetailKey(validId),
    queryFn: async () => {
      if (!validId) return null;

      const { data, error } = await supabase
        .from("proposals")
        .select(
          `
          *,
          proposal_line_items (*),
          proposal_static_pages (
            *,
            static_pages (*)
          ),
          signature_requests (*)
        `
        )
        .eq("id", validId)
        .single();

      if (error) throw error;
      if (!data) return null;

      const template = await loadTemplateForProposal(data.template_id);

      return {
        ...(data as ProposalFull),
        template,
        proposal_templates: template ?? null,
      } as ProposalFull;
    },
    enabled: !!validId,
    staleTime: 1000 * 30,
    refetchOnMount: true,
  });
}

// ─── Update proposal ──────────────────────────────────────────
export function useUpdateProposal() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Proposal>;
    }) => {
      if (!clientId) {
        throw new Error("No client ID");
      }
      if (!isUuid(id)) {
        throw new Error("Invalid proposal ID");
      }

      const { data, error } = await supabase
        .from("proposals")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .eq("client_id", clientId)
        .select()
        .single();

      if (error) throw error;
      return data as Proposal;
    },
    onSuccess: (proposal) => {
      qc.invalidateQueries({ queryKey: ["proposals", clientId, "list"] });
      qc.invalidateQueries({ queryKey: proposalDetailKey(proposal.id) });
      qc.invalidateQueries({ queryKey: pipelineStatsKey(clientId) });
      qc.invalidateQueries({ queryKey: monthlyProposalCountKey(clientId) });
    },
  });
}

// ─── Duplicate proposal ───────────────────────────────────────
export function useDuplicateProposal() {
  const clientId = useAuthStore((s) => s.clientId);
  const userId = useAuthStore((s) => s.userId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (proposalId: string) => {
      if (!clientId) {
        throw new Error("No client ID");
      }
      if (!isUuid(proposalId)) {
        throw new Error("Invalid proposal ID");
      }

      const { data: proposal, error: proposalError } = await supabase
        .from("proposals")
        .select(
          `
          *,
          proposal_line_items (*),
          proposal_static_pages (*),
          signature_requests (*)
        `
        )
        .eq("id", proposalId)
        .eq("client_id", clientId)
        .single();

      if (proposalError) throw proposalError;
      if (!proposal) throw new Error("Proposal not found");

      const { data: createdProposal, error: createError } = await supabase
        .from("proposals")
        .insert({
          client_id: clientId,
          created_by: userId ?? null,
          template_id: proposal.template_id,
          prospect_name: `${proposal.prospect_name} (Copy)`,
          prospect_email: proposal.prospect_email,
          prospect_address: proposal.prospect_address,
          prospect_city: proposal.prospect_city,
          prospect_state: proposal.prospect_state,
          prospect_zip: proposal.prospect_zip,
          project_summary: proposal.project_summary,
          ai_project_summary: proposal.ai_project_summary,
          notes: proposal.notes,
          status: "draft",
          tax_rate: proposal.tax_rate ?? 0,
          duplicated_from: proposal.id,
          version: (proposal.version ?? 1) + 1,
        })
        .select()
        .single();

      if (createError) throw createError;

      const lineItems = proposal.proposal_line_items ?? [];
      if (lineItems.length > 0) {
        const lineItemRows = lineItems.map(
          (item: Record<string, unknown>, idx: number) => ({
            proposal_id: createdProposal.id,
            service_item_id: (item.service_item_id as string | null) ?? null,
            name: (item.name as string) ?? "Untitled service",
            description: (item.description as string | null) ?? null,
            ai_generated_description:
              (item.ai_generated_description as string | null) ?? null,
            user_edited: (item.user_edited as boolean) ?? false,
            price: Number(item.price ?? 0),
            price_unit: (item.price_unit as string | null) ?? null,
            quantity: Number(item.quantity ?? 1),
            frequency: (item.frequency as string | null) ?? null,
            duration_hours:
              item.duration_hours != null ? Number(item.duration_hours) : null,
            focus_area: (item.focus_area as string | null) ?? null,
            sort_order: Number(item.sort_order ?? idx),
          })
        );

        const { error: lineItemsError } = await supabase
          .from("proposal_line_items")
          .insert(lineItemRows);

        if (lineItemsError) throw lineItemsError;
      }

      const staticPages = proposal.proposal_static_pages ?? [];
      if (staticPages.length > 0) {
        const staticPageRows = staticPages
          .map(
            (
              page: Record<string, unknown>,
              idx: number
            ):
              | {
                  proposal_id: string;
                  static_page_id: string;
                  page_order: number;
                }
              | null => {
              const staticPageId = (page.static_page_id as string | null) ?? null;
              if (!isUuid(staticPageId)) return null;

              return {
                proposal_id: createdProposal.id,
                static_page_id: staticPageId,
                page_order: Number(page.page_order ?? idx),
              };
            }
          )
          .filter(
            (
              page:
                | {
                    proposal_id: string;
                    static_page_id: string;
                    page_order: number;
                  }
                | null
            ): page is {
              proposal_id: string;
              static_page_id: string;
              page_order: number;
            } => page !== null
          );

        if (staticPageRows.length > 0) {
          const { error: pagesError } = await supabase
            .from("proposal_static_pages")
            .insert(staticPageRows);

          if (pagesError) throw pagesError;
        }
      }

      const { data: hydratedProposal, error: reloadError } = await supabase
        .from("proposals")
        .select(
          `
          *,
          proposal_line_items (*),
          proposal_static_pages (
            *,
            static_pages (*)
          ),
          signature_requests (*)
        `
        )
        .eq("id", createdProposal.id)
        .single();

      if (reloadError) throw reloadError;
      if (!hydratedProposal) throw new Error("Failed to reload duplicated proposal");

      const template = await loadTemplateForProposal(hydratedProposal.template_id);

      return {
        ...(hydratedProposal as ProposalFull),
        template,
        proposal_templates: template ?? null,
      } as ProposalFull;
    },
    onSuccess: (proposal) => {
      qc.invalidateQueries({ queryKey: ["proposals", clientId, "list"] });
      qc.invalidateQueries({ queryKey: pipelineStatsKey(clientId) });
      qc.invalidateQueries({ queryKey: monthlyProposalCountKey(clientId) });
      qc.setQueryData(proposalDetailKey(proposal.id), proposal);
    },
  });
}

// ─── Delete proposal ──────────────────────────────────────────
export function useDeleteProposal() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (proposalId: string) => {
      if (!clientId) {
        throw new Error("No client ID");
      }
      if (!isUuid(proposalId)) {
        throw new Error("Invalid proposal ID");
      }

      const { error } = await supabase
        .from("proposals")
        .delete()
        .eq("id", proposalId)
        .eq("client_id", clientId);

      if (error) throw error;
      return proposalId;
    },
    onSuccess: (proposalId) => {
      qc.removeQueries({ queryKey: proposalDetailKey(proposalId) });
      qc.invalidateQueries({ queryKey: ["proposals", clientId, "list"] });
      qc.invalidateQueries({ queryKey: pipelineStatsKey(clientId) });
      qc.invalidateQueries({ queryKey: monthlyProposalCountKey(clientId) });
    },
  });
}

// ─── Dashboard / pipeline stats ───────────────────────────────
export function usePipelineStats() {
  const clientId = useAuthStore((s) => s.clientId);

  return useQuery<ProposalPipelineStats>({
    queryKey: pipelineStatsKey(clientId),
    queryFn: async () => {
      if (!clientId) {
        return emptyPipelineStats();
      }

      const { data, error } = await supabase
        .from("proposals")
        .select("status,total_value")
        .eq("client_id", clientId);

      if (error) throw error;

      const proposals = data ?? [];

      const total = proposals.length;
      const draft = proposals.filter((p) => p.status === "draft").length;
      const sent = proposals.filter((p) => p.status === "sent").length;
      const viewed = proposals.filter((p) => p.status === "viewed").length;
      const signed = proposals.filter((p) => p.status === "signed").length;
      const declined = proposals.filter((p) => p.status === "declined").length;
      const expired = proposals.filter((p) => p.status === "expired").length;

      const totalValue = proposals.reduce(
        (sum, p) => sum + Number(p.total_value ?? 0),
        0
      );

      const signedValue = proposals
        .filter((p) => p.status === "signed")
        .reduce((sum, p) => sum + Number(p.total_value ?? 0), 0);

      const decisionCount = signed + declined;
      const conversionRate = decisionCount > 0 ? signed / decisionCount : 0;

      return {
        total,
        draft,
        sent,
        viewed,
        signed,
        declined,
        expired,
        total_value: totalValue,
        signed_value: signedValue,
        conversion_rate: conversionRate,
      };
    },
    enabled: !!clientId,
    staleTime: 1000 * 30,
    refetchOnMount: true,
  });
}

// ─── Monthly count ────────────────────────────────────────────
export function useMonthlyProposalCount() {
  const clientId = useAuthStore((s) => s.clientId);

  return useQuery<number>({
    queryKey: monthlyProposalCountKey(clientId),
    queryFn: async () => {
      if (!clientId) return 0;

      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { count, error } = await supabase
        .from("proposals")
        .select("*", { count: "exact", head: true })
        .eq("client_id", clientId)
        .gte("created_at", startOfMonth.toISOString());

      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!clientId,
    staleTime: 1000 * 30,
    refetchOnMount: true,
  });
}